import { useCallback, useEffect, useRef, useState } from 'react';
import Api from '../api/Api';
import { ICurrentUser } from '../types/user';

interface SessionManagerReturn {
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  loadingSession: boolean;
  handleLogin: () => void;
}

export function useSessionManager(user: ICurrentUser): SessionManagerReturn {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [loadingSession, setLoadingSession] = useState<boolean>(false);

  const notificationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const expirationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const periodicCheckRef = useRef<NodeJS.Timeout | null>(null);
  const activityLockRef = useRef<boolean>(false);
  const loadingSessionRef = useRef<boolean>(false);
  const eventCleanupRef = useRef<(() => void) | null>(null);
  const isAuthenticated = !!user?.authenticated;

  /**
   * Clean up all timers and event listeners
   */
  const cleanUpAllMonitoring = (): void => {
    if (notificationTimerRef.current) {
      clearTimeout(notificationTimerRef.current);
      notificationTimerRef.current = null;
    }

    if (expirationTimerRef.current) {
      clearTimeout(expirationTimerRef.current);
      expirationTimerRef.current = null;
    }

    if (checkIntervalRef.current) {
      clearTimeout(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }

    if (periodicCheckRef.current) {
      clearInterval(periodicCheckRef.current);
      periodicCheckRef.current = null;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    if (eventCleanupRef.current) {
      eventCleanupRef.current();
      eventCleanupRef.current = null;
    }
  };

  /**
   * Handle session expiration by showing the modal and stopping all monitoring
   */
  const handleSessionExpired = (): void => {
    setIsModalOpen(true);
    cleanUpAllMonitoring();
  };

  /**
   * Scheduled expiration time
   *
   * @param expirationTime - Expired time in millisecond
   */
  const scheduleExpirationNotification = (expirationTime: number): void => {
    if (expirationTimerRef.current) {
      clearTimeout(expirationTimerRef.current);
    }

    const now = Date.now();
    const timeUntilExpiration = expirationTime - now;

    if (timeUntilExpiration > 0) {
      expirationTimerRef.current = setTimeout(() => {
        handleSessionExpired();
      }, timeUntilExpiration);
    } else {
      handleSessionExpired();
    }
  };

  /**
   * Get expired time from server
   *
   * @returns Expired time in millisecond or false if failed or already expired
   */
  const fetchSession = async (): Promise<number | false> => {
    if (loadingSessionRef.current) {
      return false;
    }

    loadingSessionRef.current = true;
    setLoadingSession(true);

    try {
      const res = await Api.auth.getExpirationDate();
      const newExpiration = new Date(res as string).getTime();

      const now = Date.now();
      if (newExpiration <= now) {
        handleSessionExpired();
        return false;
      }

      return newExpiration;
    } catch (e) {
      return false;
    } finally {
      loadingSessionRef.current = false;
      setLoadingSession(false);
    }
  };

  const keepSessionAlive = useCallback(async (): Promise<boolean> => {
    const res = await Api.auth.keepSessionActive();

    return !!res;
  }, []);

  /**
   * Start periodic session check at 1-minute intervals
   * @param currentExpiration - Current known expiration time
   */
  const startPeriodicCheck = (currentExpiration: number): void => {
    if (periodicCheckRef.current) {
      clearInterval(periodicCheckRef.current);
    }

    periodicCheckRef.current = setInterval(async () => {
      try {
        const newExpiration = await fetchSession();

        if (!newExpiration) return;

        if (newExpiration !== currentExpiration) {
          cleanUpAllMonitoring();
          scheduleExpirationNotification(newExpiration);
          startMonitoring(newExpiration);
        }
      } catch (e) {
        console.log('Failed to get new expiration', e);
      }
    }, 60000);
  };

  /**
   * Monitor user activity and refresh session when past halfway to expiration
   *
   * @param expiration - Expiration time in milliseconds
   * @returns Cleanup function for event listeners
   */
  const setupActivityMonitoring = (expiration: number): (() => void) => {
    if (eventCleanupRef.current) {
      eventCleanupRef.current();
      eventCleanupRef.current = null;
    }

    const handleActivityAfterHalfway = async (): Promise<void> => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        if (activityLockRef.current) {
          return;
        }

        activityLockRef.current = true;

        const timeUntilExpiration = expiration - Date.now();

        if (timeUntilExpiration <= 0) {
          handleSessionExpired();
          activityLockRef.current = false;
          return;
        }

        if (!loadingSessionRef.current) {
          try {
            const newExpiration = await fetchSession();

            if (newExpiration && newExpiration !== expiration) {
              cleanUpAllMonitoring();
              scheduleExpirationNotification(newExpiration);
              startMonitoring(newExpiration);
            } else {
              startPeriodicCheck(expiration);
            }
          } finally {
            activityLockRef.current = false;
          }
        } else {
          activityLockRef.current = false;
        }
      }, 500);
    };

    const events = ['click', 'keypress', 'mousemove'];
    events.forEach((e) =>
      window.addEventListener(e, handleActivityAfterHalfway)
    );

    eventCleanupRef.current = () => {
      events.forEach((e) =>
        window.removeEventListener(e, handleActivityAfterHalfway)
      );
    };

    return eventCleanupRef.current;
  };

  /**
   * Start monitoring activity
   *
   * @param initialExpiration - Initial expiration in millisecond
   */
  const startMonitoring = (initialExpiration: number): void => {
    const now = Date.now();
    if (initialExpiration <= now) {
      handleSessionExpired();
      return;
    }

    const duration = initialExpiration - now;
    const halfwayPoint = now + duration / 2;
    const initialDelay = Math.max(0, halfwayPoint - now);

    checkIntervalRef.current = setTimeout(() => {
      setupActivityMonitoring(initialExpiration);
    }, initialDelay);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      cleanUpAllMonitoring();
      return;
    }

    let active = true;

    fetchSession().then((expiration) => {
      if (!active || !expiration) return;
      scheduleExpirationNotification(expiration);
      startMonitoring(expiration);
    });

    return () => {
      active = false;
      cleanUpAllMonitoring();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const runPeriodically = async () => {
      try {
        await keepSessionAlive();
      } catch (error) {
        console.error('Error keeping session alive:', error);
      }
    };

    runPeriodically();

    const interval = setInterval(runPeriodically, 300000);

    return () => {
      clearInterval(interval);
    };
  }, [isAuthenticated, keepSessionAlive]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const onUnauthorized = () => {
      setIsModalOpen(true);
      cleanUpAllMonitoring();
    };

    window.addEventListener('mood:session-unauthorized', onUnauthorized);
    return () =>
      window.removeEventListener('mood:session-unauthorized', onUnauthorized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleLogin = (): void => {
    (window.top || window).location.assign(
      `${Api.SERVER_DOMAIN}${Api.IISSite}/auth/signin?redirectUrl=${encodeURIComponent(window.location.origin)}`
    );
  };

  return {
    isModalOpen,
    setIsModalOpen,
    loadingSession,
    handleLogin,
  };
}
